import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { kucoinApiEndpoint } from '../constants'
import { errorResponseSchema } from '../schema'
import { createPrivateGetHeaders } from '../utils'
import { type Trade, tradeResponseSchema } from './schema'

const urlPath = '/api/v1/fills'

type FetchSpotTradeHistoriesParameters = {
  apiKey: string
  apiSecret: string
  password: string
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

/** {@link https://www.kucoin.com/docs/rest/spot-trading/fills/get-filled-list}
 * - Request via this endpoint to get the recent fills.
 * - The return value is the data after Pagination, sorted in descending order according to time.
 * - Data time range The system allows you to retrieve data up to one week (start from the last day by default). If the time period of the queried data exceeds one week (time range from the start time to end time exceeded 7*24 hours), the system will prompt to remind you that you have exceeded the time limit. If you only specified the start time, the system will automatically calculate the end time (end time = start time + 7 * 24 hours). On the contrary, if you only specified the end time, the system will calculate the start time (start time= end time - 7 * 24 hours) the same way.
 */
export const fetchSpotTradeHistories = ({
  apiKey,
  apiSecret,
  password,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchSpotTradeHistoriesParameters): Promise<readonly Trade[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 500) {
        throw new Error('limit must be less than or equal to 500')
      }

      const url = new URL(urlPath, kucoinApiEndpoint)
      url.searchParams.append('pageSize', limit.toString())
      url.searchParams.append('startAt', from.toString())
      url.searchParams.append('endAt', to.toString())
      url.searchParams.append('tradeType', 'TRADE')
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                passphrase: password,
                urlPath,
                query: query.length > 0 ? query : '',
              })),
            },
          },
          schema: z.union([tradeResponseSchema, errorResponseSchema]),
        },
      )
      if ('msg' in response) {
        throw new Error(response.msg)
      }
      return response.data.items
    },
  })
